import React from "react";
import moment from "moment";
import { formatFieldValue } from "../utils/date";
import { Divider, Grid, Header, Icon } from "semantic-ui-react";
import { Permit } from "../models/Permit";
import ViewItem from "./common/ViewItem";
import ServerImage from "../components/common/ServerImage";

interface Props {
  showHeader: boolean;
  permit: Permit;
  organizationLogoUrl?: string | null;
  showAffectedServices?: boolean;
  showAsBuiltRequired?: boolean;
}

const PermitDescriptionView = ({
  permit,
  showHeader,
  organizationLogoUrl,
  showAffectedServices,
  showAsBuiltRequired,
}: Props) => {
  return (
    <>
      {showHeader && (
        <>
          <Header as="h3">
            {organizationLogoUrl && <ServerImage floated="right" src={organizationLogoUrl} size="large" />}
            <Icon name="angle right" />
            <Header.Content> Permit description</Header.Content>
          </Header>
          <Divider />
        </>
      )}
      <Grid columns="2">
        <Grid.Row>
          <Grid.Column>
            <ViewItem header="Contractor" value={permit.contractor} />
          </Grid.Column>
          <Grid.Column>
            <ViewItem header="Sub-contractor" value={permit.subContractor} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <ViewItem header="Works description" value={permit.worksDescription} />
          </Grid.Column>
          <Grid.Column>
            <ViewItem header="Location of work" value={permit.locationOfWork} />
          </Grid.Column>
        </Grid.Row>
        <Divider horizontal content="Project" />
        <Grid.Row>
          <Grid.Column>
            <ViewItem header="Project name" value={permit.project.name} />
          </Grid.Column>
          <Grid.Column>
            <ViewItem header="Project no." value={permit.project.number} />
          </Grid.Column>
        </Grid.Row>
        <Divider />
        <Grid.Row>
          <Grid.Column>
            <ViewItem
              header="Duration"
              value={`${moment.utc(permit.validityDuration.from).local().format("L")} to ${moment
                .utc(permit.validityDuration.to)
                .local()
                .format("L")}`}
            />
          </Grid.Column>
          <Grid.Column>
            <ViewItem
              header="Hours"
              value={`${String(moment.duration(permit.validityDuration?.hoursFrom).hours()).padStart(2, "0")}:${String(
                moment.duration(permit.validityDuration?.hoursFrom).minutes(),
              ).padStart(2, "0")} to 
                 ${String(moment.duration(permit.validityDuration?.hoursTo).hours()).padStart(2, "0")}:${String(
                   moment.duration(permit.validityDuration?.hoursTo).minutes(),
                 ).padStart(2, "0")}${permit.validityDuration?.endsNextDay ? " (next day)" : ""}`}
            />
          </Grid.Column>
        </Grid.Row>
        {permit.template?.fields &&
          permit.template.fields.length > 0 &&
          [...permit.template.fields]
            .sort((a, b) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
            .reduce<React.ReactNode[]>((rows, field, i, arr) => {
              if (i % 2 === 0) {
                const next = arr[i + 1];
                const fv = permit.fieldValues?.find((v) => v.fieldKey === field?.key);
                const nextFv = next ? permit.fieldValues?.find((v) => v.fieldKey === next?.key) : undefined;
                rows.push(
                  <Grid.Row key={field?.key}>
                    <Grid.Column>
                      <ViewItem header={field?.label ?? field?.key ?? ""} value={formatFieldValue(field, fv)} />
                    </Grid.Column>
                    <Grid.Column>
                      {next && (
                        <ViewItem header={next?.label ?? next?.key ?? ""} value={formatFieldValue(next, nextFv)} />
                      )}
                    </Grid.Column>
                  </Grid.Row>,
                );
              }
              return rows;
            }, [])}
        {(showAffectedServices || showAsBuiltRequired) && (
          <Grid.Row>
            {showAffectedServices && (
              <Grid.Column>
                <ViewItem
                  header="Affected services"
                  value={permit.noAffectedServices ? "No affected services" : permit.affectedServices.join(", ")}
                />
              </Grid.Column>
            )}
            {showAsBuiltRequired && (
              <Grid.Column>
                <ViewItem header="As-Built required" value={permit.asBuiltRequired ? "Yes" : "No"} />
              </Grid.Column>
            )}
          </Grid.Row>
        )}
        <Grid.Row columns="1">
          <Grid.Column>
            <ViewItem header="Comments" value={permit.comments} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default PermitDescriptionView;
